import { useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Music2, Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";

export default function BackingTrackPanel({ song, onChange }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const hasTrack = !!song?.backing_track_path;

  const pickFile = () => inputRef.current?.click();

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 30 * 1024 * 1024) {
      toast.error("File too large (30MB max).");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/songs/${song.id}/backing-track`, form);
      toast.success("Backing track uploaded.");
      onChange?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const remove = async () => {
    try {
      await api.delete(`/songs/${song.id}/backing-track`);
      toast.success("Backing track removed.");
      onChange?.();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="border border-border rounded-2xl p-5 bg-background" data-testid="backing-track-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-secondary text-primary shrink-0">
            <Music2 size={18} />
          </span>
          <div>
            <p className="font-medium">Backing track</p>
            <p className="text-sm text-muted-foreground mt-0.5">
              {hasTrack
                ? "Plays while you sing so Sing It mode has real music behind it."
                : "Upload an MP3, M4A, WAV, OGG or FLAC so Sing It plays music behind your voice."}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            accept="audio/mpeg,audio/mp3,audio/mp4,audio/x-m4a,audio/aac,audio/wav,audio/x-wav,audio/ogg,audio/webm,audio/flac"
            className="hidden"
            onChange={upload}
            data-testid="backing-track-input"
          />
          <Button
            size="sm"
            variant={hasTrack ? "outline" : "default"}
            className="rounded-full"
            onClick={pickFile}
            disabled={uploading}
            data-testid="backing-track-upload-btn"
          >
            <Upload size={14} className="mr-1.5" />
            {uploading ? "Uploading…" : hasTrack ? "Replace" : "Upload track"}
          </Button>
          {hasTrack && (
            <Button
              size="icon"
              variant="ghost"
              className="rounded-full text-muted-foreground hover:text-destructive"
              onClick={remove}
              data-testid="backing-track-delete-btn"
              aria-label="Remove backing track"
            >
              <Trash2 size={14} />
            </Button>
          )}
        </div>
      </div>

      {hasTrack && (
        <audio
          controls
          preload="none"
          className="mt-4 w-full"
          src={`${API_BASE}/songs/${song.id}/backing-track`}
          data-testid="backing-track-preview"
        />
      )}
    </div>
  );
}
